import { boardFileToDoc, encodeVersion } from '@os/doc';
import type { Api } from '../../lib/api';
import { systemTemplate, type FilledTemplateId, type TemplateLabels } from './system';

export type TemplateChoice = { kind: 'blank' } | { kind: 'system'; id: FilledTemplateId } | { kind: 'custom'; boardId: string };

/**
 * A new board from a template (spec §8). Blank: the board only. System: the board, then its room seeded with the
 * template's file (a failed seed moves the board to the trash, as an import does). Custom: a duplicate of the template
 * board made by the Worker, so its assets are shared, then moved into the folder (plan P10 Ruling 7).
 */
export async function instantiateTemplate(o: {
  /** `getProfile`: the maker, recorded as the author of a system template's objects (P5d). */
  api: Pick<Api, 'createBoard' | 'seedBoard' | 'duplicateBoard' | 'updateBoard' | 'lifecycle'> & Partial<Pick<Api, 'getProfile'>>;
  orgId: string;
  projectId: string;
  folderId: string | null;
  title: string;
  choice: TemplateChoice;
  labels: TemplateLabels;
}): Promise<{ boardId: string }> {
  const { api, choice } = o;
  if (choice.kind === 'custom') {
    const { boardId } = await api.duplicateBoard(choice.boardId, { projectId: o.projectId, title: o.title });
    if (o.folderId) await api.updateBoard(boardId, { folderId: o.folderId }).catch(() => undefined);
    return { boardId };
  }
  const board = await api.createBoard({ orgId: o.orgId, projectId: o.projectId, folderId: o.folderId, title: o.title });
  if (choice.kind === 'blank') return { boardId: board.id };
  try {
    // The person who makes the board adds its objects (P5d, Ruling 7).
    const me = api.getProfile ? await api.getProfile().then((p) => p.id, () => null) : null;
    const stamp = me ? { createdBy: me, createdAt: Date.now() } : undefined;
    await api.seedBoard(board.id, await encodeVersion(boardFileToDoc(systemTemplate(choice.id, o.labels), new Map(), stamp)));
  } catch (err) {
    await api.lifecycle('board', 'trash', board.id).catch(() => undefined);
    throw err;
  }
  return { boardId: board.id };
}
